import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { IMG } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const WHATSAPP_URL =
  "https://wa.me/5547997920909?text=Ol%C3%A1%2C%20acabei%20de%20garantir%20meu%20acesso%20ao%20Clube%2C%20como%20fa%C3%A7o%20para%20verificar%20os%20numeros%20da%20sorte%20e%20meus%20benef%C3%ADcios%3F";

export default function Obrigado() {
  useEffect(() => {
    document.title = "Obrigado! · Dive Master";
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Purchase", { currency: "BRL", value: 580 });
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden bg-[#04102A] px-6 py-14 text-white"
      data-testid="obrigado-page"
    >
      {/* Radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 20%, rgba(123,224,214,0.18), transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(245,197,66,0.12), transparent 60%)",
        }}
      />

      {/* Sparkle wave divider at top */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24" aria-hidden="true">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="block h-full w-full">
          <defs>
            <linearGradient id="obGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#7BE0D6" />
              <stop offset="50%" stopColor="#F5C542" />
              <stop offset="100%" stopColor="#7BE0D6" />
            </linearGradient>
          </defs>
          <path
            d="M0,20 C240,60 480,0 720,30 C960,60 1200,0 1440,25 L1440,32 L0,32 Z"
            fill="url(#obGrad)"
            opacity="0.85"
          />
        </svg>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative z-10 flex w-full max-w-lg flex-col items-center text-center"
      >
        <img
          src={IMG.logo}
          alt="Dive Master"
          className="h-24 w-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)] sm:h-28"
          data-testid="obrigado-logo"
        />

        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="mt-10 flex h-24 w-24 items-center justify-center rounded-full bg-[#7BE0D6] shadow-2xl shadow-[#7BE0D6]/40"
        >
          <CheckCircle2 className="h-14 w-14 text-[#04102A]" strokeWidth={2.4} />
        </motion.div>

        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#F5C542]/40 bg-[#F5C542]/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.24em] text-[#F5C542] backdrop-blur"
        >
          <Sparkles className="h-3.5 w-3.5" /> Pagamento confirmado
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-5 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl"
          data-testid="obrigado-title"
        >
          Obrigado por entrar no{" "}
          <span className="text-[#7BE0D6]">Clube Dive Master</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-5 text-base leading-relaxed text-white/85 sm:text-lg"
        >
          Fale com a nossa equipe pelo WhatsApp para confirmar seus Números da Sorte e conhecer seus benefícios.
        </motion.p>

        <motion.a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="mt-10 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] px-8 py-5 text-base font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-[#25D366]/40 sm:w-auto sm:text-lg"
          data-testid="obrigado-whatsapp"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.2-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.412-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.463 3.488" />
          </svg>
          Falar no WhatsApp
          <ArrowRight className="h-5 w-5" />
        </motion.a>

        <p className="mt-6 text-xs text-white/50">
          Salve nosso número na sua agenda para não perder novidades.
        </p>
      </motion.div>

      {/* Bottom wave */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16" aria-hidden="true">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="block h-full w-full">
          <path
            d="M0,80 C240,120 480,40 720,70 C960,100 1200,40 1440,70 L1440,120 L0,120 Z"
            fill="rgba(123,224,214,0.06)"
          />
        </svg>
      </div>
    </main>
  );
}
